import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  MessageCircle,
  Home,
  Settings,
  BookOpen,
  Cpu,
  Layers,
  Users as UsersIcon,
  Globe,
  Database,
  Building2,
  KeyRound,
} from "lucide-react";
import InviktusLogo from "@/components/InviktusLogo";
import UserMenu from "@/components/UserMenu";
import { useAuth } from "@/contexts/AuthContext";
import LlmHealthBanner from "@/components/LlmHealthBanner";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useMyCompany, canSeeKeys } from "@/hooks/useMyCompany";

/**
 * Persistent top navigation for internal Inviktus surfaces.
 * Light variant for white pages; "dark" variant for the agent chat page.
 */
export default function TopNav({ variant = "light", right = null }) {
  const loc = useLocation();
  const { user } = useAuth();
  const dark = variant === "dark";
  const { data: myCompany } = useMyCompany();

  const linkClass = (active) =>
    `relative inline-flex items-center gap-2 px-3 py-1.5 text-[12px] font-medium transition ${
      dark
        ? active
          ? "text-white"
          : "text-slate-400 hover:text-white"
        : active
        ? "text-ink"
        : "text-slate-500 hover:text-ink"
    }`;

  const underline = (active) =>
    active ? (
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-2 -bottom-[14px] h-[2px] rounded-full bg-gradient-to-r from-accent-blue to-accent-purple"
      />
    ) : null;

  const items = [
    { to: "/", label: "Inicio", icon: Home, match: (p) => p === "/" },
    {
      to: "/dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      match: (p) => p.startsWith("/dashboard"),
    },
    {
      to: "/consulta",
      label: "Consultoría",
      icon: MessageCircle,
      match: (p) => p.startsWith("/consulta") || p.startsWith("/informe"),
    },
    {
      to: "/mi-empresa",
      label: "Mi empresa",
      icon: Building2,
      match: (p) => p === "/mi-empresa",
      requires: ["cliente"],
    },
    ...(canSeeKeys(myCompany)
      ? [{
          to: "/mi-empresa/claves",
          label: "Claves IA",
          icon: KeyRound,
          match: (p) => p.startsWith("/mi-empresa/claves"),
          requires: ["cliente"],
        }]
      : []),
    {
      to: "/admin/clientes",
      label: "Clientes",
      icon: Building2,
      match: (p) => p.startsWith("/admin/clientes"),
      requires: ["admin"],
    },
    {
      to: "/admin/kbl",
      label: "KBL",
      icon: BookOpen,
      match: (p) => p.startsWith("/admin/kbl"),
      requires: ["admin", "consultor"],
    },
    {
      to: "/admin/llm",
      label: "LLM",
      icon: Cpu,
      match: (p) => p.startsWith("/admin/llm"),
      requires: ["admin"],
    },
    {
      to: "/admin/industria",
      label: "Industrias",
      icon: Layers,
      match: (p) => p.startsWith("/admin/industria"),
      requires: ["admin", "consultor"],
    },
    {
      to: "/admin/usuarios",
      label: "Usuarios",
      icon: UsersIcon,
      match: (p) => p.startsWith("/admin/usuarios"),
      requires: ["admin"],
    },
    {
      to: "/admin/tenants",
      label: "Tenants",
      icon: Globe,
      match: (p) => p.startsWith("/admin/tenants"),
      requires: ["admin"],
    },
    {
      to: "/admin/demo",
      label: "Demo",
      icon: Database,
      match: (p) => p.startsWith("/admin/demo"),
      requires: ["admin"],
    },
    {
      to: "/admin/facturacion",
      label: "Admin",
      icon: Settings,
      match: (p) =>
        p.startsWith("/admin") &&
        !p.startsWith("/admin/kbl") &&
        !p.startsWith("/admin/clientes") &&
        !p.startsWith("/admin/llm") &&
        !p.startsWith("/admin/industria") &&
        !p.startsWith("/admin/usuarios") &&
        !p.startsWith("/admin/tenants") &&
        !p.startsWith("/admin/demo"),
      requires: ["admin"], // Fix 7 (A1): plans and billing are admin only
    },
  ].filter((it) => !it.requires || (user && it.requires.includes(user.role)));

  return (
    <header
      className={`sticky top-0 z-30 border-b backdrop-blur-xl ${
        dark
          ? "border-white/10 bg-ink/80 text-slate-100"
          : "border-slate-200/70 bg-white/85 text-ink"
      } print:hidden`}
      data-testid="top-nav"
    >
      <div className="mx-auto flex max-w-screen-2xl items-center justify-between gap-4 px-6 py-3 lg:px-10">
        <div className="flex min-w-0 items-center gap-8">
          {/* fix 13b (1): the logo never shrinks (it was squeezed to ~5 px by the 11 admin links) */}
          <Link
            to="/"
            className="inline-flex shrink-0 items-center"
            aria-label="Inviktus home"
          >
            <InviktusLogo size={32} />
          </Link>
          <nav className="hidden items-center gap-1 sm:flex">
            {items.map(({ to, label, icon: Icon, match }) => {
              const active = match(loc.pathname);
              return (
                <Link
                  key={to}
                  to={to}
                  className={linkClass(active)}
                  data-testid={`nav-${to.replace(/\W+/g, "") || "home"}`}
                  title={label}
                >
                  <Icon className="h-3.5 w-3.5" /> <span className={right ? "sr-only" : (items.length > 7 ? "hidden 2xl:inline" : "hidden xl:inline")}>{label}</span>
                  {underline(active)}
                </Link>
              );
            })}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          {right}
          <ThemeToggle />
          <UserMenu variant={variant} />
        </div>
      </div>
      <LlmHealthBanner />
    </header>
  );
}
